// client/src/features/repairs/enums.ts — the two wire enums SCR-REP-001/002 render · MAINT-007.
//
// Both are GENERATED unions (`@/client`), and both are declared here as `as const satisfies
// readonly T[]`, which is the shape `features/contracts/enums.ts` established: the array is a
// literal tuple the code can iterate, and a member the contract adds or drops is a compile
// error in the PR that regenerated the client rather than a filter that quietly stops offering
// a state.
//
// `MeterSubmissionChannel` is the ticket's `reportedVia`. Its name is the meter reading's,
// because MAINT-003 reused the enum rather than adding a fourth spelling of the same three
// channels — D-960 records why the source is decided by the route and never sent by a client.

import type {
  MeterSubmissionChannel,
  RepairCategory,
  RepairTicketStatus,
  RepairUrgency,
} from '@/client';
import type { IconName } from '@/components/icons';

/**
 * 03 §11.5's status set, in the order the board draws its columns: the life of an incident from
 * „deschis" to one of its two closures. It is NOT the transition table — that is the server's
 * (`RepairTicketRules`), and it reaches the screen as `allowedStatuses` on every payload. Four
 * members since „ofertat" was folded into „în lucru" (D-2740).
 */
export const REPAIR_TICKET_STATUSES = [
  'OPEN',
  'IN_PROGRESS',
  'DONE',
  'CANCELLED',
] as const satisfies readonly RepairTicketStatus[];

/** Where the incident came from: the owner's own screen, the tenant portal, a no-account link. */
export const REPORTED_VIA_CHANNELS = [
  'OWNER',
  'TENANT_PORTAL',
  'PUBLIC_LINK',
] as const satisfies readonly MeterSubmissionChannel[];

/**
 * The two states a ticket never leaves (`RepairTicketRules.IsTerminal`). Read here to decide
 * whether the detail screen still offers an edit — the server refuses the write with
 * `repair-ticket-closed`, and a button that only ever answers 422 is a worse answer than no
 * button.
 */
export function isTerminal(status: RepairTicketStatus): boolean {
  return status === 'DONE' || status === 'CANCELLED';
}

/**
 * What kind of incident a ticket is (D-2370), in the order the tenant's question lists them: the
 * four trades, then „Altele" last because it is the answer when none of them fits. The same five
 * the server's `RepairCategory` declares, in the same order.
 */
export const REPAIR_CATEGORIES = [
  'PLUMBING',
  'ELECTRICAL',
  'HEATING',
  'APPLIANCE',
  'OTHER',
] as const satisfies readonly RepairCategory[];

/**
 * The icon beside each category, on the tenant's pills and on every chip that names one (D-2374).
 * A wrench for the plumber because the set has no tap and a beaker reads as a laboratory; a bolt,
 * a flame and a box for the other three trades; a question mark for „Altele".
 */
export const REPAIR_CATEGORY_ICON: Record<RepairCategory, IconName> = {
  PLUMBING: 'wrench',
  ELECTRICAL: 'bolt',
  HEATING: 'fire',
  APPLIANCE: 'cube',
  OTHER: 'question-mark-circle',
};

/** Whether a string a form is holding is one of the five. */
export function isRepairCategory(value: string): value is RepairCategory {
  return (REPAIR_CATEGORIES as readonly string[]).includes(value);
}

/**
 * How fast a ticket must be fixed (D-2470), in the order the tenant's question lists them: the
 * one that cannot wait first. The same three the server's `RepairUrgency` declares, in the same
 * order. The windows behind them (24 hours, 7 days, 30 days) are the server's alone — the screens
 * show the fix-by the server computed and never the arithmetic (D-2473).
 */
export const REPAIR_URGENCIES = ['URGENT', 'NORMAL', 'LOW'] as const satisfies readonly RepairUrgency[];

/** The level a report is filed with when nobody chose one — the server's default, mirrored (D-2472). */
export const DEFAULT_REPAIR_URGENCY: RepairUrgency = 'NORMAL';

/**
 * The icon beside each level, on the tenant's pills and on the chip (D-2477): a flag for the one
 * that cannot wait, a clock for the ordinary term, a calendar for the one that can.
 */
export const REPAIR_URGENCY_ICON: Record<RepairUrgency, IconName> = {
  URGENT: 'flag',
  NORMAL: 'clock',
  LOW: 'calendar',
};

/** Whether a string a form is holding is one of the three. */
export function isRepairUrgency(value: string): value is RepairUrgency {
  return (REPAIR_URGENCIES as readonly string[]).includes(value);
}
